import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Badge, Modal, Descriptions, Tag, Typography, message } from 'antd';
import { bookingAPI } from '../../utils/api';
import { Booking } from '../../types';
import dayjs, { Dayjs } from 'dayjs';

const { Title } = Typography;

type BadgeStatus = 'success' | 'error' | 'warning' | 'default';

interface CalendarViewProps {
  active: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({ active }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedBookings, setSelectedBookings] = useState<Booking[]>([]);
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (active === 'calendar' && !hasFetchedRef.current) {
      fetchBookings();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchBookings = async () => {
    try {
      const response = await bookingAPI.getCalendar();
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      message.error('Failed to load bookings');
    }
  };

  const getListData = (value: Dayjs): Booking[] => {
    return bookings.filter(booking => {
      const bookingDate = dayjs(booking.date);
      // Check if the booking date is the same as the calendar date (same day, month, and year)
      return bookingDate.isSame(value, 'day');
    });
  };

  const getStatusBadge = (status: string): BadgeStatus => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const dateCellRender = (value: any) => {
    const listData = getListData(value);
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {listData.slice(0, 3).map((item, index) => (
          <li key={index} style={{ marginBottom: 4 }}>
            <Badge 
              status={getStatusBadge(item.status)} 
              text={
                <span style={{ fontSize: '12px' }}>
                  {item.customerName} - {item.numberOfPeople}p
                </span>
              }
            />
          </li>
        ))}
        {listData.length > 3 && (
          <li style={{ fontSize: '12px', color: '#999' }}>
            +{listData.length - 3} more...
          </li>
        )}
      </ul>
    );
  };

  const onSelect = (value: any) => {
    const dayBookings = getListData(value);
    if (dayBookings.length > 0) {
      setSelectedBookings(dayBookings);
      setSelectedDate(value);
      setDetailModalVisible(true);
    }
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>Calendar View - One-Day Trips</Title>
      
      <Calendar
        dateCellRender={dateCellRender}
        onSelect={onSelect}
      />

      <Modal
        title={`Bookings on ${selectedDate.format('MMMM DD, YYYY')}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedBookings.map((booking, index) => (
          <div 
            key={booking._id} 
            style={{ 
              marginBottom: index < selectedBookings.length - 1 ? 24 : 0,
              paddingBottom: index < selectedBookings.length - 1 ? 24 : 0,
              borderBottom: index < selectedBookings.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}
          >
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Customer" span={2}>
                {booking.customerName}
              </Descriptions.Item>
              <Descriptions.Item label="Package">
                {booking.packageName}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={
                  booking.status === 'pending' ? 'orange' :
                  booking.status === 'accepted' ? 'green' : 'red'
                }>
                  {booking.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Trip Date" span={2}>
                {dayjs(booking.date).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="People" span={2}>
                {booking.numberOfPeople} person(s)
              </Descriptions.Item>
            </Descriptions>
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default CalendarView;